import React, { useState, useEffect } from "react";
import { Modal, message } from "antd";
import { useNavigate } from "react-router-dom";
import { renderGrid } from "../common/FormRenderer";
import { ChevronDownIcon, ChevronUpIcon, Settings } from "lucide-react";
import {
  objectiveSections,
  initialFormState,
  PO_PICKLIST_LABELS,
} from "../../config/projectObjectiveFields";
import {
  buildInfoFieldsForCreate,
  buildRequirementsFieldsForCreate,
  buildProductivityFieldsForCreate,
  buildFunnelFieldsForCreate,
  buildRatesFieldsForCreate,
  buildActionRulesFieldsForCreate,
  buildSystemFieldsForCreate,
} from "./columnsConfig";
import { usePicklistMap, resolvePicklistFields } from "../../hooks/usePicklist";
import { SOBJECT_PROJECT_OBJECTIVE } from "../../config/sobjectNames";
import { createIndividualProjectObjective } from "../../services/projectObjectiveService";
import { validateProjectObjectiveEditForm } from "../../config/projectObjectiveEdit.validation";
import { getPreferredDefaultWorkTypeValue } from "../../config/projectObjectiveDefaults.js";

const EMPTY_PICKLIST = [];

export default function CreateProjectObjectiveModal({
  visible,
  onClose,
  onSuccess,
  initialValues,
  projectId,
}) {
  const [form, setForm] = useState(initialFormState);
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const navigate = useNavigate();

  const { picklistMap } = usePicklistMap(
    Object.values(PO_PICKLIST_LABELS),
    SOBJECT_PROJECT_OBJECTIVE,
  );
  const workTypePicklist =
    picklistMap[PO_PICKLIST_LABELS.WORK_TYPE] ?? EMPTY_PICKLIST;
  const [validationErrors, setValidationErrors] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    information: true,
    requirements: false,
    productivity: false,
    funnel: false,
    rates: false,
    actionRules: false,
    admin: false,
    system: false,
  });
  const [advancedExpandedSections, setAdvancedExpandedSections] = useState({
    funnel: false,
    rates: false,
    system: false,
  });

  useEffect(() => {
    if (!visible) return;
    let merged = { ...initialFormState };
    if (projectId) merged.project = projectId;
    if (initialValues && typeof initialValues === "object") {
      let values = { ...initialValues };
      const { projectId: ivPid, projectName, startDate, endDate } = values;
      if (ivPid) {
        values = {
          project: projectName,
          projectId: ivPid,
          dateStart: startDate,
          dateEnd: endDate,
          ...values,
        };
      }
      merged = { ...merged, ...values };
    }

    setForm(merged);
  }, [visible, initialValues, projectId]);

  useEffect(() => {
    if (!visible) return;
    const preferred = getPreferredDefaultWorkTypeValue(workTypePicklist);
    if (!preferred) return;
    setForm((prev) => {
      if ((prev.workType ?? "").trim() !== "") return prev;
      return { ...prev, workType: preferred };
    });
  }, [visible, workTypePicklist]);

  const handleSave = async () => {
    // Prepare working copy; auto-generate Objective Name if blank
    let working = { ...form };
    try {
      const nameEmpty =
        !working.objectiveName ||
        (typeof working.objectiveName === "string" &&
          working.objectiveName.trim() === "");
      if (nameEmpty && working.project) {
        // Fetch project to get Short Name
        const { fetchProject } = await import("../../services/projectService");
        const proj = await fetchProject(working.projectId);
        const shortName = (
          proj?.info?.shortName ||
          proj?.info?.name ||
          ""
        ).trim();
        const language = (working.language || "").trim();
        const country = (working.country || "").trim();
        const appendToObjectiveName = (
          working.appendToObjectiveName || ""
        ).trim();
        const langCountry =
          language && country
            ? `${language}-${country}`
            : language || country || "";
        const generated =
          shortName && langCountry
            ? `${shortName} : ${langCountry}${appendToObjectiveName}`
            : shortName
              ? `${shortName}${appendToObjectiveName}`
              : langCountry
                ? `${langCountry}${appendToObjectiveName}`
                : "Project Objective";
        working = { ...working, objectiveName: generated };
      }
    } catch (e) {
      // If we cannot generate, continue with user-provided values
      // eslint-disable-next-line no-console
      console.warn("Could not auto-generate Objective Name:", e);
    }
    // Validate form before saving (with auto-generated name if applicable)
    const validationPayload = {
      info: {
        name: working.objectiveName,
        projectId: working.projectId, // ensure we validate against the selected Project Id
        contributorFacingName: working.contributorFacingName,
        dateStart: working.dateStart,
        dateEnd: working.dateEnd,
      },
      requirements: {
        workType: working.workType,
        country: working.country || "",
        language: working.language || "",
        includeInCountryList: working.includeInCountryList || "",
        excludeStates: working.excludeStates || "",
        listOfCountries: working.listOfCountries || "",
        currentlyResidingUsState: working.currentlyResidingUsState || "",
      },
      productivity: {
        productivityTargetType: working.productivityTargetType,
      },
      diversity: {
        minimumContributors: working.minimumContributors,
      },
      actionRules: {
        daysBetweenReminderEmails: working.daysBetweenReminderEmails,
      },
      funnelSplits: {
        funnelA: form.funnelAPercentage,
        funnelB: form.funnelBPercentage,
        funnelC: form.funnelCPercentage,
        funnelD: form.funnelDPercentage,
        funnelE: form.funnelEPercentage,
      },
      targets: {
        productionPaymentThreshold: working.productionPaymentThreshold,
        qualityPaymentThreshold: working.qualityPaymentThreshold,
      },
    };

    const errors = validateProjectObjectiveEditForm(validationPayload, true);

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Extra guardrails not covered by centralized rules:
    // 1) Ensure a Project was selected from lookup (has an Id)
    if (!form.project || String(form.project).trim() === "") {
      setValidationErrors({
        "info.projectId": "Project: Please select a Project from the lookup",
      });
      return;
    }
    // 2) Ensure at least one of Country or Language is provided (defensive double-check)
    const countryEmpty = !form.country || String(form.country).trim() === "";
    const languageEmpty = !form.language || String(form.language).trim() === "";
    if (countryEmpty && languageEmpty) {
      setValidationErrors({
        "requirements.country": "Either Country or Language must be filled",
        "requirements.language": "Either Country or Language must be filled",
      });
      return;
    }

    setValidationErrors({});
    setSaving(true);
    try {
      // Call the API to create the project objective
      const result = await createIndividualProjectObjective(working);

      console.log("✅ API Response received:", result);
      console.log("✅ Result type:", typeof result);
      console.log("✅ Is Array?:", Array.isArray(result));
      console.log(
        "✅ Result keys:",
        result ? Object.keys(result) : "null/undefined",
      );
      console.log("✅ Full result object:", JSON.stringify(result, null, 2));

      // Handle different response formats:
      // 1. Array with object: [{ Id: "...", ... }]
      // 2. Direct object: { Id: "...", ... }
      // 3. Envelope: { success: true, data: { Id: "..." } }
      let objectiveData = null;

      if (Array.isArray(result) && result.length > 0 && result[0]?.Id) {
        objectiveData = result[0];
        console.log("✅ Extracted from array:", objectiveData.Id);
      } else if (result?.Id) {
        objectiveData = result;
        console.log("✅ Using direct object:", objectiveData.Id);
      } else if (result?.data?.Id) {
        objectiveData = result.data;
        console.log("✅ Extracted from envelope:", objectiveData.Id);
      }

      const isSuccess = objectiveData && objectiveData.Id;
      console.log(
        "✅ isSuccess check:",
        isSuccess,
        "Objective ID:",
        objectiveData?.Id,
      );

      if (isSuccess) {
        console.log("✅ SUCCESS PATH - showing success message");
        message.success("Project Objective created successfully!");
        // Reset form
        setForm(initialFormState);
        setShowAdvanced(false);
        setExpandedSections({
          information: true,
          requirements: false,
          productivity: false,
          funnel: false,
          rates: false,
          actionRules: false,
          admin: false,
          system: false,
        });
        setAdvancedExpandedSections({
          funnel: false,
          rates: false,
          system: false,
        });

        // Call onSuccess callback if provided
        if (typeof onSuccess === "function") {
          try {
            onSuccess();
          } catch (cbError) {
            console.error("❌ Error in onSuccess callback:", cbError);
          }
        }

        // Close modal
        onClose();
        navigate(`/po/${objectiveData?.Id}`);
      } else {
        console.error("❌ API returned unexpected response structure:", result);
        console.error("❌ Expected an object with Id field, got:", {
          type: typeof result,
          isArray: Array.isArray(result),
          keys: result ? Object.keys(result) : "null",
        });
        const errorMsg =
          result?.error?.message ||
          result?.message ||
          "Failed to create Project Objective. Response did not contain an Id.";
        message.error(errorMsg);
      }
    } catch (error) {
      console.error("❌ Error creating project objective:", error);
      message.error(
        error.message ||
          "An error occurred while creating the Project Objective",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form
    setForm(initialFormState);
    setValidationErrors({});
    setShowAdvanced(false);
    setExpandedSections({
      information: true,
      requirements: false,
      productivity: false,
      funnel: false,
      rates: false,
      actionRules: false,
      admin: false,
      system: false,
    });
    setAdvancedExpandedSections({
      funnel: false,
      rates: false,
      admin: false,
      system: false,
    });
    onClose();
  };

  const handleChange = (path, value, displayName) => {
    if (Object.keys(validationErrors).length > 0) {
      setValidationErrors({});
    }

    setForm((prev) => {
      const keys = path.split(".");
      let updated;
      if (keys.length === 1) {
        updated = { ...prev, [path]: value };
      } else {
        updated = { ...prev };
        let current = updated;
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          current[key] = { ...current[key] };
          current = current[key];
        }
        current[keys[keys.length - 1]] = value;
      }
      if (displayName !== undefined) {
        updated[`${path}DisplayName`] = displayName;
      }
      if (path === "country") {
        if (value !== "" && value !== null) {
          updated.includeInCountryList = false;
          updated.listOfCountries = [];
        }
        if (value !== "United States") {
          updated.excludeStates = false;
          updated.currentlyResidingUsState = [];
        }
      }

      return updated;
    });
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleAdvancedSection = (section) => {
    setAdvancedExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const SectionHeader = ({ title, section }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-2.5 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
    >
      <span className="text-sm font-semibold text-gray-700">{title}</span>
      {expandedSections[section] ? (
        <ChevronUpIcon className="w-4 h-4 text-gray-400" />
      ) : (
        <ChevronDownIcon className="w-4 h-4 text-gray-400" />
      )}
    </button>
  );

  const AdvancedSectionHeader = ({ title, section }) => (
    <button
      type="button"
      onClick={() => toggleAdvancedSection(section)}
      className="w-full flex items-center justify-between p-2.5 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
    >
      <span className="text-sm font-semibold text-gray-700">{title}</span>
      {advancedExpandedSections[section] ? (
        <ChevronUpIcon className="w-4 h-4 text-gray-400" />
      ) : (
        <ChevronDownIcon className="w-4 h-4 text-gray-400" />
      )}
    </button>
  );

  // Define which sections are "advanced"
  const advancedSectionKeys = ["funnel", "rates", "system"];

  // Convert objectiveSections object to array and filter into basic and advanced
  const sectionsArray = Object.values(objectiveSections);
  const basicSections = sectionsArray.filter(
    (s) => !advancedSectionKeys.includes(s.key),
  );
  const advancedSections = sectionsArray.filter((s) =>
    advancedSectionKeys.includes(s.key),
  );

  // Get validation error messages directly from centralized validation
  const errorMessages = Object.values(validationErrors);

  return (
    <Modal
      title="Create Project Objective"
      open={visible}
      onOk={handleSave}
      onCancel={handleCancel}
      okText="Save"
      cancelText="Cancel"
      confirmLoading={saving}
      width={900}
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto", padding: 0 }}
    >
      {/* Validation Error Banner */}
      {errorMessages.length > 0 && (
        <div className="sticky top-0 z-10 px-6 py-3 bg-red-50 border-b border-red-200">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 flex-shrink-0 text-red-600 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-red-800 mb-1.5">
                {errorMessages.length} error
                {errorMessages.length !== 1 ? "s" : ""} found:
              </h4>
              <div className="max-h-24 overflow-y-auto pr-2">
                <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                  {errorMessages.map((error, index) => (
                    <li key={index} className="leading-relaxed">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Content */}
      <div className="space-y-3 p-6">
        {/* Basic Sections */}
        {basicSections.map((section) => (
          <div key={section.key}>
            <SectionHeader title={section.title} section={section.key} />
            {expandedSections[section.key] && (
              <div className="mt-2">
                {renderGrid(
                  resolvePicklistFields(
                    section.key === "information"
                      ? buildInfoFieldsForCreate()
                      : section.key === "requirements"
                        ? buildRequirementsFieldsForCreate({ form: form })
                        : section.key === "productivity"
                          ? buildProductivityFieldsForCreate(form)
                          : section.key === "funnel"
                            ? buildFunnelFieldsForCreate()
                            : section.key === "rates"
                              ? buildRatesFieldsForCreate()
                              : section.key === "actionRules"
                                ? buildActionRulesFieldsForCreate()
                                : section.key === "system"
                                  ? buildSystemFieldsForCreate()
                                  : section.fields,
                    picklistMap,
                  ),
                  {
                    formData: form,
                    onChange: handleChange,
                    showValidation: true,
                  },
                )}
              </div>
            )}
          </div>
        ))}

        {/* Advanced Fields Button */}
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full px-4 py-3 border-2 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 hover:shadow-md"
            style={{
              borderColor: "#08979c",
              color: "#08979c",
              backgroundColor: showAdvanced ? "#e6fffb" : "white",
            }}
          >
            <Settings className="w-5 h-5" />
            <span className="font-medium">Advanced Fields</span>
            <span className="text-sm text-gray-500">(Optional)</span>
          </button>
        </div>

        {/* Advanced Fields Content */}
        {showAdvanced && (
          <div className="space-y-3 pt-4">
            {advancedSections.map((section) => (
              <div key={section.key}>
                <AdvancedSectionHeader
                  title={section.title}
                  section={section.key}
                />
                {advancedExpandedSections[section.key] && (
                  <div className="mt-2">
                    {renderGrid(
                      resolvePicklistFields(
                        section.key === "information"
                          ? buildInfoFieldsForCreate()
                          : section.key === "requirements"
                            ? buildRequirementsFieldsForCreate({ form })
                            : section.key === "productivity"
                              ? buildProductivityFieldsForCreate()
                              : section.key === "funnel"
                                ? buildFunnelFieldsForCreate()
                                : section.key === "rates"
                                  ? buildRatesFieldsForCreate()
                                  : section.key === "actionRules"
                                    ? buildActionRulesFieldsForCreate()
                                    : section.key === "system"
                                      ? buildSystemFieldsForCreate()
                                      : section.fields,
                        picklistMap,
                      ),
                      {
                        formData: form,
                        onChange: handleChange,
                        showValidation: true,
                      },
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
